import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { buildStartupNavItems } from '@ideoz/ui';
import { useStartups } from '@ideoz/core';

/**
 * The startup shell's sidebar nav items — Work Scopes / Artifacts / Knowledge
 * Hub / Design system / Startup Profile / Integrations — targeting the user's
 * first startup with no active section.
 *
 * Account-level pages that aren't nested under a startup (`/profile`,
 * `/billing`) use this so they wear the same sidebar as the startup shell
 * instead of the bare "New / My Projects" standard nav.
 */
export function useStartupSidebarNavItems() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: startups = [] } = useStartups();
  const startupSlug = startups[0]?.slug ?? '';

  return useMemo(
    () =>
      buildStartupNavItems({
        t,
        activeSection: '' as any,
        onNavigateToHome: startupSlug ? () => navigate(`/${startupSlug}/home`) : undefined,
        onNavigateToWorkScopes: startupSlug
          ? () => navigate(`/${startupSlug}/work-scopes`)
          : undefined,
        onNavigateToArtifacts: startupSlug
          ? () => navigate(`/${startupSlug}/artifacts`)
          : undefined,
        onNavigateToFiles: startupSlug ? () => navigate(`/${startupSlug}/files`) : undefined,
        onNavigateToData: startupSlug ? () => navigate(`/${startupSlug}/data`) : undefined,
        onNavigateToDesignSystem: startupSlug
          ? () => navigate(`/${startupSlug}/design-system`)
          : undefined,
        onNavigateToStartupProfile: startupSlug
          ? () => navigate(`/${startupSlug}/profile`)
          : undefined,
        onNavigateToIntegrations: startupSlug
          ? () => navigate(`/${startupSlug}/integrations`)
          : undefined,
      }),
    [startupSlug, navigate, t]
  );
}
